import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"

export function relativeTime(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

// Every data surface on the routing pages says when it was fetched and can be
// refreshed in place. Confidence comes from stating freshness, not implying it.
export function AsOf({
  fetchedAt,
  onRefresh,
}: {
  fetchedAt: Date | undefined
  onRefresh: () => void
}) {
  if (!fetchedAt) return null
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Typography variant="caption" color="text.secondary">
        as of{" "}
        {fetchedAt.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </Typography>
      <IconButton size="small" onClick={onRefresh} title="Refresh">
        <RefreshIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Stack>
  )
}

// Where a lead's attribute values came from and how old they were at the
// decision: read from HubSpot, sent by the triggering workflow, or the
// original signup answers.
export function ValuesChip({
  source,
  syncedAt,
}: {
  source: string | null | undefined
  syncedAt: string | null | undefined
}) {
  const effective = source ?? "signup"
  const age = syncedAt ? ` · ${relativeTime(syncedAt)}` : ""
  const [label, explanation] =
    effective === "hubspot"
      ? [`HubSpot${age}`, "Routed on values read from HubSpot at that moment."]
      : effective === "trigger"
      ? [
          `sent by workflow${age}`,
          "The HubSpot workflow that triggered this assignment sent these values with its request.",
        ]
      : [
          "signup answers",
          "Routed on the lead's original signup answers. No newer values were available.",
        ]
  return (
    <Tooltip title={explanation}>
      <Chip
        size="small"
        variant="outlined"
        label={label}
        color={effective === "signup" ? "default" : "success"}
        sx={{ height: 20, fontSize: "0.7rem" }}
      />
    </Tooltip>
  )
}
